import { memo } from "react"
import type {
	InterpolatedCursor,
	CursorUserInfo,
} from "../../hooks/cursor/types"

interface RemoteCursorProps {
	cursor: InterpolatedCursor
	userInfo: CursorUserInfo
	showDebug?: boolean
}

export const RemoteCursor = memo(function RemoteCursor({
	cursor,
	userInfo,
	showDebug = false,
}: RemoteCursorProps) {
	const { position, state, draggedNodes, connectingFrom } = cursor
	const { name, color } = userInfo

	// Cursor SVG based on state
	const getCursorSvg = () => {
		if (state === "dragging") {
			return (
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
					{/* Grabbing hand cursor */}
					<path
						d="M12 3C10.9 3 10 3.9 10 5V11H8C6.9 11 6 11.9 6 13V15C6 18.31 8.69 21 12 21C15.31 21 18 18.31 18 15V8C18 5.79 16.21 4 14 4C13.27 4 12.6 4.22 12.03 4.59C11.81 3.68 11 3 10 3H12ZM14 6C15.1 6 16 6.9 16 8V15C16 17.21 14.21 19 12 19C9.79 19 8 17.21 8 15V13H12V5C12 5 13 5 14 6Z"
						fill={color}
						stroke="white"
						strokeWidth="0.5"
					/>
				</svg>
			)
		}

		if (state === "selecting") {
			return (
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
					{/* Pointer with selection box */}
					<path
						d="M2 2L8 18L10.5 11L18 9L2 2Z"
						fill={color}
						stroke="white"
						strokeWidth="1.5"
						strokeLinejoin="round"
					/>
					<rect
						x="10"
						y="10"
						width="8"
						height="8"
						fill="none"
						stroke={color}
						strokeWidth="1.5"
						strokeDasharray="2 2"
					/>
				</svg>
			)
		}

		if (state === "connecting") {
			return (
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
					{/* Pointer with connection line */}
					<path
						d="M2 2L8 18L10.5 11L18 9L2 2Z"
						fill={color}
						stroke="white"
						strokeWidth="1.5"
						strokeLinejoin="round"
					/>
					{/* Connection indicator - a small circle with a line */}
					<circle
						cx="18"
						cy="18"
						r="4"
						fill="none"
						stroke={color}
						strokeWidth="2"
					/>
					<line
						x1="12"
						y1="12"
						x2="14"
						y2="14"
						stroke={color}
						strokeWidth="2"
						strokeLinecap="round"
					/>
				</svg>
			)
		}

		// Default pointer cursor
		return (
			<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
				<path
					d="M2 2L8.5 18L10.5 11L18 9L2 2Z"
					fill={color}
					stroke="white"
					strokeWidth="1.5"
					strokeLinejoin="round"
				/>
			</svg>
		)
	}

	const getStateLabel = () => {
		if (state === "dragging" && draggedNodes && draggedNodes.length > 0) {
			return "(dragging)"
		}
		if (state === "selecting") {
			return "(selecting)"
		}
		if (state === "connecting" && connectingFrom) {
			return "(connecting)"
		}
		return null
	}

	const stateLabel = getStateLabel()

	return (
		<div
			className="pointer-events-none absolute z-50"
			style={{
				transform: `translate(${position.x}px, ${position.y}px)`,
				transition: "transform 16ms linear",
			}}
		>
			{/* Cursor icon */}
			<div className="relative">
				{getCursorSvg()}

				{/* Name label */}
				<div
					className="absolute left-5 top-5 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium text-white shadow-sm"
					style={{ backgroundColor: color }}
				>
					{name}
					{stateLabel && <span className="ml-1 opacity-75">{stateLabel}</span>}
				</div>

				{/* State indicator dot */}
				{(state === "selecting" ||
					state === "dragging" ||
					state === "connecting") && (
					<div
						className="absolute -right-1 -top-1 h-2 w-2 rounded-full animate-pulse"
						style={{ backgroundColor: color }}
					/>
				)}

				{/* Debug info */}
				{showDebug && (
					<div className="absolute left-5 top-10 whitespace-nowrap rounded bg-black/80 px-1 py-0.5 text-[10px] text-white">
						{Math.round(position.x)}, {Math.round(position.y)}
					</div>
				)}
			</div>
		</div>
	)
})
